'use client';

import { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, Gift, Sparkles, Copy, Check, Users, 
  TrendingUp, Award, UserCheck, Share2, Play,
  BookOpen, Ticket, Zap
} from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { toast } from 'sonner'

type ActivityLog = {
  id: string
  text: string
  time: string
  type: 'click' | 'signup' | 'unlock'
}

type RewardTier = {
  count: number
  reward: string
  description: string
  icon: React.ComponentType<any>
}

const REWARDS: RewardTier[] = [
  { count: 1, reward: 'Creator PDF Toolkit', description: 'Unlock instant download access.', icon: BookOpen },
  { count: 5, reward: '20% Lifetime Discount', description: 'Promo code automatically emailed.', icon: Ticket },
  { count: 10, reward: '1-Month Pro Access', description: 'Account upgraded automatically.', icon: Zap }
]

const getLogIcon = (type: 'click' | 'signup' | 'unlock') => {
  switch (type) {
    case 'unlock':
      return <Sparkles className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
    case 'signup':
      return <UserCheck className="w-3.5 h-3.5 text-primary flex-shrink-0" />
    case 'click':
    default:
      return <Share2 className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
  }
}

export default function ReferralMarketingClient() {
  const [referralsCount, setReferralsCount] = useState(0)
  const [copied, setCopied] = useState(false)
  const [isSimulating, setIsSimulating] = useState(false)
  const [logs, setLogs] = useState<ActivityLog[]>([
    { id: '1', text: 'Alex shared referral link on Twitter', time: '10m ago', type: 'click' }
  ])

  const handleCopyLink = () => {
    navigator.clipboard.writeText('https://lenklyst.com/ref/alex')
    setCopied(true)
    toast.success('Referral link copied!')
    setTimeout(() => setCopied(false), 2000)
  }

  const triggerReferralSimulation = () => {
    if (isSimulating) return
    setIsSimulating(true)
    
    // Step 1: Click log
    const names = ['Sophia', 'David', 'Emma', 'Daniel', 'Olivia', 'Lucas', 'Liam', 'Zoe']
    const selectedName = names[Math.floor(Math.random() * names.length)]
    
    toast.info(`Simulating referral activity for ${selectedName}...`)

    setTimeout(() => {
      const clickLog: ActivityLog = {
        id: Date.now().toString(),
        text: `${selectedName} clicked your referral link`,
        time: 'Just now',
        type: 'click'
      }
      setLogs(prev => [clickLog, ...prev])
      
      // Step 2: Signup log
      setTimeout(() => {
        const signupLog: ActivityLog = {
          id: (Date.now() + 1).toString(),
          text: `${selectedName} registered a new account`,
          time: 'Just now',
          type: 'signup'
        }
        setLogs(prev => [signupLog, ...prev])
        setReferralsCount(prev => {
          const nextCount = prev + 1
          
          // Check reward unlock
          const unlockedTier = REWARDS.find(t => t.count === nextCount)
          if (unlockedTier) {
            setTimeout(() => {
              const unlockLog: ActivityLog = {
                id: (Date.now() + 2).toString(),
                text: `Tier Unlocked: ${unlockedTier.reward}!`,
                time: 'Just now',
                type: 'unlock'
              }
              setLogs(prevLogs => [unlockLog, ...prevLogs])
              toast.success(`Milestone Unlocked! ${unlockedTier.reward}`)
            }, 500)
          }

          return nextCount
        })

        setIsSimulating(false)
      }, 1200)

    }, 600)
  }

  const resetSimulation = () => {
    setReferralsCount(0)
    setLogs([{ id: '1', text: 'Alex shared referral link on Twitter', time: '10m ago', type: 'click' }])
    toast.success('Simulation dashboard reset!')
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  }

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white">
      <GeometricBackground />
      <MarketingNavbar />

      <main id="main-content">
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 px-6 max-w-[1400px] mx-auto z-10 flex flex-col items-center">
          <motion.div 
            className="text-center space-y-6 max-w-3xl"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="flex items-center gap-2 justify-center">
              <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
                Viral Growth Software
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.1]">
              Referral Program Software<br />
              <span className="text-gray-500">Viral systems to recruit</span><br />
              Brand Advocates
            </motion.h1>

            <motion.p variants={itemVariants} className="text-base sm:text-lg text-white/50 font-light max-w-xl mx-auto leading-relaxed">
              Turn users into growth advocates. Build milestones rewards structures, track sharing signups, and generate compounding organic traffic.
            </motion.p>
          </motion.div>

          {/* Dynamic Split Screen Sandbox */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="w-full max-w-5xl mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch"
          >
            {/* Left Panel: Milestone Rewards */}
            <div className="lg:col-span-7 bg-background/40 border border-white/10 rounded-3xl p-6 md:p-8 backdrop-blur-xl space-y-6 shadow-[0_0_80px_rgba(0,102,255,0.05)] flex flex-col justify-between">
              <div>
                <h2 className="text-sm font-mono tracking-widest text-primary/80 uppercase flex items-center gap-2 mb-6">
                  <Sparkles className="w-4 h-4" /> Gamified Rewards Sandbox
                </h2>

                <div className="space-y-4">
                  {/* Referral Link Card */}
                  <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Share2 className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-white/40 uppercase">Your Referral Link</p>
                        <span className="text-xs font-semibold font-mono text-white">
                          https://lenklyst.com/ref/alex
                        </span>
                      </div>
                    </div>
                    <button 
                      onClick={handleCopyLink}
                      className="w-full sm:w-auto px-4 py-2 bg-white text-black hover:bg-white/90 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 transition-all active:scale-95 font-sans"
                    >
                      {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copied ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>

                  {/* Milestone list */}
                  <div className="space-y-3 pt-2">
                    <h3 className="text-xs font-mono text-white/40 uppercase">Goal Tiers Tracker</h3>
                    <div className="space-y-2.5">
                      {REWARDS.map((tier) => {
                        const isUnlocked = referralsCount >= tier.count
                        const RewardIcon = tier.icon
                        return (
                          <div 
                            key={tier.count} 
                            className={`p-3.5 rounded-2xl border transition-all duration-300 flex items-center justify-between ${
                              isUnlocked 
                                ? 'bg-emerald-500/5 border-emerald-500/30' 
                                : 'bg-white/[0.01] border-white/5 opacity-60'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                isUnlocked ? 'bg-emerald-500/10 text-emerald-400' : 'bg-white/5 text-white/40'
                              }`}>
                                <RewardIcon className="w-4 h-4" />
                              </div>
                              <div>
                                <p className={`text-xs font-bold ${isUnlocked ? 'text-emerald-300' : 'text-white/80'}`}>{tier.reward}</p>
                                <p className="text-[10px] text-white/40">{tier.description}</p>
                              </div>
                            </div>
                            <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ${
                              isUnlocked ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/10 text-white/50'
                            }`}>
                              {isUnlocked ? 'Unlocked' : `${referralsCount}/${tier.count} Signups`}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-white/5 flex flex-col sm:flex-row gap-2">
                <button 
                  id="simulate-referral-btn"
                  onClick={triggerReferralSimulation}
                  disabled={isSimulating}
                  className="flex-1 py-3 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all shadow-[0_0_20px_rgba(0,102,255,0.3)] active:scale-95 disabled:opacity-50"
                >
                  <Play className="w-4 h-4 fill-current" />
                  <span>{isSimulating ? 'Simulating advocate clicks...' : 'Simulate Referral Conversion'}</span>
                </button>
                <button 
                  onClick={resetSimulation}
                  className="py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold rounded-xl text-xs transition-all active:scale-95"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Right Panel: Live Stats Feed */}
            <div className="lg:col-span-5 bg-background/30 border border-white/10 rounded-3xl p-6 md:p-8 backdrop-blur-xl flex flex-col justify-between shadow-[0_0_80px_rgba(0,102,255,0.05)] min-h-[400px]">
              <div>
                <div className="flex justify-between items-center border-b border-white/5 pb-4 mb-4">
                  <span className="text-[10px] font-mono text-white/40 uppercase">Advocate Stats</span>
                  <div className="flex items-center gap-1.5 text-xs text-primary font-bold">
                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    Live Activity Feed
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="p-3 bg-white/[0.01] border border-white/5 rounded-2xl text-center">
                    <Users className="w-4 h-4 text-primary mx-auto mb-1" />
                    <p className="text-[9px] text-white/40 uppercase">Total Referrals</p>
                    <p className="text-xl font-bold font-mono text-white mt-0.5">{referralsCount}</p>
                  </div>
                  <div className="p-3 bg-white/[0.01] border border-white/5 rounded-2xl text-center">
                    <TrendingUp className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                    <p className="text-[9px] text-white/40 uppercase">Conversion Rate</p>
                    <p className="text-xl font-bold font-mono text-emerald-400 mt-0.5">34.8%</p>
                  </div>
                </div>

                {/* Feed logs */}
                <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                  <AnimatePresence>
                    {logs.map((log) => (
                      <motion.div 
                        key={log.id}
                        initial={{ opacity: 0, x: -10, y: -5 }}
                        animate={{ opacity: 1, x: 0, y: 0 }}
                        exit={{ opacity: 0 }}
                        className={`p-2.5 rounded-xl border text-[11px] font-mono flex items-center justify-between gap-2 ${
                          log.type === 'unlock' 
                            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300 font-semibold'
                            : log.type === 'signup'
                            ? 'bg-primary/5 border-primary/20 text-white'
                            : 'bg-white/[0.01] border-white/5 text-white/50'
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          {getLogIcon(log.type)}
                          <span className="truncate">{log.text}</span>
                        </div>
                        <span className="text-[8px] text-white/30 whitespace-nowrap">{log.time}</span>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>

              <div className="text-[9px] text-white/30 font-mono mt-4 text-center border-t border-white/5 pt-4">
                All transactions are isolated simulation events.
              </div>
            </div>
          </motion.div>
        </section>

        {/* Benefits */}
        <section className="py-20 border-t border-white/5 relative z-10">
          <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold uppercase tracking-tight">Fraud Prevention Filters</h3>
              <p className="text-sm text-white/50 font-light leading-relaxed">
                Block referral spam automatically. Our fraud filters monitor click rates, duplicate browser sessions, and network IPs to ensure verified organic growth.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Gift className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold uppercase tracking-tight">Dynamic Reward Milestones</h3>
              <p className="text-sm text-white/50 font-light leading-relaxed">
                Design multi-tier loyalty ladders. Automatically email promo codes, issue coupon links, or unlock files when users invite their colleagues.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold uppercase tracking-tight">Compounding Growth Loop</h3>
              <p className="text-sm text-white/50 font-light leading-relaxed">
                Build a self-sustaining viral loop. Newly referred users are prompted with their own referral keys immediately, amplifying brand outreach.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 border-t border-white/5 text-center relative z-10 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.05),transparent_70%)] blur-[100px] pointer-events-none" />
          <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">Grow organically</h2>
          <p className="text-white/40 text-sm max-w-md mx-auto mt-4 font-light">Set up a high-converting referral loyalty program. Reward advocates and drive viral brand shares.</p>
          <Link 
            href="/auth/sign-up" 
            className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 bg-primary text-white font-bold rounded-full hover:scale-105 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] active:scale-95"
          >
            Build Referral Program <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>

      <MarketingFooter />
    </div>
  )
}
